import UserwalkthroughType from "../../types/generic/UserWalkthroughType";
import { t } from "i18next";
import { ReactComponent as LaunchWalkthrough } from "../../images/launchWalkthrough.svg";
import { ModalPopup } from "../common";
import { useState } from "react";
import DeclinedWalkthrough from "./DeclinedWalkthrough";
import { useCheckMobileScreen } from "../../hooks";

interface UserWalkthroughIntroPopUpProps {
  walkthroughType: UserwalkthroughType;
  isOpen: boolean;
  onOpenChange(isOpen: boolean): void;
  runWalkthrough(run: boolean): void;
  /** Turning the tour down (or dismissing this prompt) counts as having seen it. */
  onDismissed(): void;
  onWalkthroughFinished(): void;
}

function UserWalkthroughIntroPopUp({
  walkthroughType,
  isOpen,
  onOpenChange,
  runWalkthrough,
  onDismissed,
  onWalkthroughFinished,
}: UserWalkthroughIntroPopUpProps) {
  const isMobile = useCheckMobileScreen();
  const [runDeclinedWalkthrough, setRunDeclinedWalkthrough] =
    useState<boolean>(false);

  const confirmStartWalkthrough = () => {
    onOpenChange(false);
    runWalkthrough(true);
  };

  const confirmStopWalkthrough = () => {
    onOpenChange(false);
    runWalkthrough(false);
    onDismissed();
    setRunDeclinedWalkthrough(true);
  };

  /**
   * Only fires for an Esc key press or a click outside the prompt - the buttons
   * above close it directly. Treated as a dismissal, minus the "if you change your
   * mind" pointer that declining shows.
   */
  const handleModalOpenChange = (open: boolean) => {
    onOpenChange(open);
    if (!open) {
      onDismissed();
      onWalkthroughFinished();
    }
  };

  const myDashboardModalContent = (
    <div className="text-center">
      <p className="text-2xl font-semibold primary-text mb-2 heading">
        {t("Walkthrough.MyDashboard.Title")}
      </p>
      <p className="p-4">{t("Walkthrough.MyDashboard.Intro.Text")}</p>
      <p className="font-semibold">
        {t("Walkthrough.MyDashboard.WouldYouLikeATour")}
      </p>
    </div>
  );

  const managerDashboardModalContent = (
    <div className="text-center">
      <p className="text-2xl font-semibold primary-text mb-2 heading">
        {t("Walkthrough.ManagerDashboard.Title")}
      </p>
      <p className="p-4">{t("Walkthrough.ManagerDashboard.Intro.Text")}</p>
      <p className="font-semibold">
        {t("Walkthrough.ManagerDashboard.WouldYouLikeATour")}
      </p>
    </div>
  );

  const analyticsFiltersModalContent = (
    <div className="text-center">
      <p className="text-2xl font-semibold primary-text mb-2 heading">
        {t("Walkthrough.AnalyticsFilters.Title")}
      </p>
      <p className="p-4">{t("Walkthrough.AnalyticsFilters.Intro.Text")}</p>
      <p className="font-semibold">
        {t("Walkthrough.AnalyticsFilters.WouldYouLikeATour")}
      </p>
    </div>
  );

  const introContent =
    walkthroughType === "MANAGER-DASHBOARD"
      ? managerDashboardModalContent
      : walkthroughType === "ANALYTICS-FILTERS"
        ? analyticsFiltersModalContent
        : myDashboardModalContent;

  return (
    <>
      <ModalPopup
        isOpen={isOpen}
        onOpenChange={handleModalOpenChange}
        title={""}
        showCloseIcon={false}
        width="TINY"
      >
        <LaunchWalkthrough width="5rem" height="7rem" className="m-auto" />
        {introContent}
        <div className="flex flex-row mt-6 py-4 items-center justify-center gap-6">
          <button
            className="btn-primary max-w-fit"
            onClick={confirmStartWalkthrough}
          >
            {t("Walkthrough.Intro.Options.YesPlease")}
          </button>
          <button
            className="text-gray-400 max-w-fit hover:cursor-pointer hover:underline"
            onClick={confirmStopWalkthrough}
          >
            {t("Walkthrough.Intro.Options.NoThanks")}
          </button>
        </div>
      </ModalPopup>

      {runDeclinedWalkthrough && (
        <DeclinedWalkthrough
          walkthroughType={walkthroughType}
          onFinished={onWalkthroughFinished}
        />
      )}
    </>
  );
}

export default UserWalkthroughIntroPopUp;
